import {Component, computed, OnDestroy, OnInit, signal} from '@angular/core';
import {IconeService} from '../services/icone/icone.service';
import {InitialAuthService} from "../shared/auth/initial-auth.service";
import {FooterComponent} from './footer/footer.component';
import {Router, RouterOutlet} from '@angular/router';
import {NavbarComponent} from './navbar/navbar.component';
import {MatSidenavModule} from '@angular/material/sidenav';
import {HeaderComponent} from './header/header.component';
import {InactivityService} from '../services/inactivity/inactivity.service';
import {UtilisateurConnecte} from '../models/utilisateurConnecte/UtilisateurConnecte';
import {UtilisateurService} from '../services/utilisateurs/utilisateur.service';
import {BehaviorSubject, EMPTY, forkJoin, Subscription, switchMap} from 'rxjs';
import {Utilisateur} from '../models/utilisateur/UtilisateurResponse';
import {
  ProfilsEntitesUtilisateursService
} from '../services/profils-entites-utilisateurs/profils-entites-utilisateurs.service';
import {ProfilsEntitesUtilisateur} from '../models/profilsEntitesUtilisateur/ProfilsEntitesUtilisateurResponse';
import {Adb2cService} from '../services/adb2c/adb2c.service';
import {Adb2c} from '../models/adb2c/Adb2cResponse';
import {ProfilService} from '../services/profil/profil.service';

export const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [HeaderComponent, MatSidenavModule, NavbarComponent, RouterOutlet, FooterComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'opus';
  collapsed = signal(false);
  sidenavWidth = computed(() => this.collapsed() ? '70px' : '355px');
  currentUser: Utilisateur | null = null;
  currentUserAdb2c: Adb2c | undefined;
  currentUserLabel: string | undefined;
  profilsEntitiesUser: ProfilsEntitesUtilisateur[] = [];
  isUserLoaded: boolean = false;
  entityWithProfiles: any[] = [];
  principalEntityId: number | undefined;
  currentProfile: string | undefined;
  userSelectedProfil: BehaviorSubject<string> = new BehaviorSubject<string>('');
  utilisateurConnecte: UtilisateurConnecte = {
    name: this.authService.connectedUser$.getValue()?.name!,
    given_name: this.authService.connectedUser$.getValue()?.given_name!,
    mail: this.authService.connectedUser$.getValue()?.mail!,
    idAdb2c: this.authService.connectedUser$.getValue()?.idAdb2c!,
    mobilePhone: this.authService.connectedUser$.getValue()?.mobilePhone!
  }
  private userSubscription: Subscription = new Subscription();
  private profilEntitySubscription: Subscription = new Subscription();
  private adb2cSubscription: Subscription = new Subscription();
  private userUpdateSubscription: Subscription = new Subscription();

  constructor(public iconService: IconeService, private authService: InitialAuthService,
              private inactivityService: InactivityService,
              private utilisateurService: UtilisateurService,
              private profilsEntitesUtilisateursService: ProfilsEntitesUtilisateursService,
              private adb2cService: Adb2cService,
              private profilService: ProfilService,
              private router: Router) {
  }

  ngOnInit() {
    this.iconService.registerIcons();
    this.currentUserLabel = `${this.utilisateurConnecte.name} ${this.utilisateurConnecte.given_name}`;
    this.inactivityService.startWatching();
    this.loadUserDetails();
  }

  handleToggleCollapse() {
    this.collapsed.set(!this.collapsed());
  }

  onProfileChanged(newProfile: string): void {
    this.currentProfile = newProfile;
    this.profilService.setCurrentProfile(newProfile);
    this.userSelectedProfil.next(newProfile);
  }

  ngOnDestroy() {
    this.inactivityService.stopWatching();
    this.userSubscription.unsubscribe();
    this.profilEntitySubscription.unsubscribe();
    this.adb2cSubscription.unsubscribe();
    this.userUpdateSubscription.unsubscribe();
  }

  private loadUserDetails() {
    if (this.utilisateurConnecte.mail) {
      this.userSubscription.add(
        this.utilisateurService.getUsers({mail: this.utilisateurConnecte.mail})
          .pipe(
            switchMap((response) => {
              const user = response.data[0];
              const exist = user.profils.some(profil => profil.id_application === 2);
              if (user && exist) {
                this.isUserLoaded = true;
                this.currentUser = user;
                this.utilisateurService.setCurrentUser(this.currentUser.id);
                this.authService.currentUser$.next(user);
                console.log('Current User connected :  ', this.currentUser);
                this.principalEntityId = user.entite_id;
                return this.profilsEntitesUtilisateursService.getProfilEntityUser({users_id: user.id});
              } else {
                this.router.navigate(['/account-not-found']);
                return EMPTY;
              }
            })
          )
          .subscribe({
            next: (response) => {
              this.profilsEntitiesUser = response.data;
              console.log('Current User connected profiles :  ', this.profilsEntitiesUser);
              this.entityWithProfiles = this.groupProfilesByEntity();
              console.log('Current User connected profiles by Entity:', this.entityWithProfiles);
              this.checkIfIdb2cNull();
            },
            error: (error) => {
              console.error('Error loading user details or profiles', error);
            }
          })
      );
    }
  }

  private checkIfIdb2cNull() {
    if (this.currentUser?.keolisAADAccount! && !this.currentUser?.id_adb2c && !this.currentUser?.userPrincipalName) {
      console.log('keolisAADAccount is true, id_adb2c and userPrincipalName are null, adb2c/user linking process begin');
      if (this.currentUser?.mail) {
        this.adb2cSubscription.add(
          this.adb2cService.getAdb2cById(this.utilisateurConnecte.idAdb2c).subscribe({
            next: (response) => {
              this.currentUserAdb2c = response.data;
              console.log('ADB2C Data:', this.currentUserAdb2c);
              if (this.currentUserAdb2c) {
                this.linkAdb2cToUserInterne(this.currentUser?.id.toString()!, {
                  ...this.currentUser,
                  id_adb2c: this.currentUserAdb2c.id,
                  userPrincipalName: this.currentUserAdb2c.userPrincipalName
                });
              }
            },
            error: (error) => console.error('Error fetching ADB2C data:', error)
          })
        );
      }
    } else {
      console.log('Adb2c is linked to intern account');
    }
  }

  private linkAdb2cToUserInterne(userId: string, updatedUserDetails: any): void {
    let updates = [];
    const updateUser$ = this.utilisateurService.updateUser(userId, {
      givenName: updatedUserDetails.givenName,
      surname: updatedUserDetails.surname,
      displayName: updatedUserDetails.displayName,
      mail: updatedUserDetails.mail,
      mobilePhone: updatedUserDetails.mobilePhone,
      accountEnabled: updatedUserDetails.accountEnabled,
      keolisAADAccount: updatedUserDetails.keolisAADAccount,
      entite_id: updatedUserDetails.entite_id,
      userPrincipalName: updatedUserDetails.userPrincipalName,
      id_adb2c: updatedUserDetails.id_adb2c
    });
    updates.push(updateUser$);

    const updateAdb2c$ = this.adb2cService.updateAdb2c(updatedUserDetails.id_adb2c, {
      givenName: updatedUserDetails.givenName,
      surname: updatedUserDetails.surname,
      displayName: updatedUserDetails.displayName,
      mailNickname: updatedUserDetails.mailNickname,
      mobilePhone: updatedUserDetails.mobilePhone,
      mail: updatedUserDetails.mail
    });
    updates.push(updateAdb2c$);

    this.userUpdateSubscription.add(
      forkJoin(updates).subscribe({
        next: (responses) => {
          console.log('User and ADB2C linked successfully', responses);
        },
        error: (error) => {
          console.error('Error updating user or ADB2C:', error);
        }
      })
    );
  }

  private groupProfilesByEntity(): { entity: string, entityId: number, profiles: string[] }[] {
    if (!this.currentUser) {
      return [];
    }

    const validProfilIds = this.currentUser.profils
    .filter(profil => profil.id_application === 2)
    .map(profil => profil.id);

    const filteredProfiles = this.profilsEntitiesUser.filter(
      (item) => validProfilIds.includes(item.profil_id)
    );

    const profilesByEntity = filteredProfiles.reduce<{
      [key: string]: { entityId: number, profiles: string[] }
    }>((acc, item) => {
      const entityLabel = item.entity.libelle;
      const entityId = item.entite_id;
      const profilLabel = item.profil.libelle_profil;

      if (!acc[entityLabel]) {
        acc[entityLabel] = {entityId: entityId, profiles: []};
      }
      acc[entityLabel].profiles.push(profilLabel);
      return acc;
    }, {});

    return Object.entries(profilesByEntity).map(([entity, data]) => ({
      entity: entity,
      entityId: data.entityId,
      profiles: data.profiles
    }));
  }
}
